import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { usePersons } from '../data/people.ts'
import { fmtNum, parseDecimal } from '../lib/format.ts'
import { BackLink, Banner, DisabledHint, Field, NotAllowed } from '../ui/components.tsx'
import { StepUpField, useSensitive } from '../ui/sensitive.tsx'
import { isErased } from './PersonPrivacy.tsx'          // an erased person is never given pay again (the server refuses: PERSON_ERASED)
import { NoFactorBanner } from './people/Factor.tsx'

interface Rate { person_id: string; display_name: string; amount: number; unit: 'hour' | 'month'; currency: string; valid_from: string }

/** Pay rates (GK-001): the direction's alone. Never in the device's mirror and never in a sync profile: the rates are read from the server on request, after a
 *  fresh second-factor code, and each read is audited (who, when, which device; never the amounts). The staff's personal contacts moved into each person's
 *  information sheet in M20 (Employés; person_file.set@1); person_contact.set@1 stays for older builds.
 *  M23 item 24: without pay.read the page is the "Vous n'avez pas accès" card, not a six-digit field and a red 400; item 32: a missing second factor is said
 *  before the form. */
export function PayPage() {
  const { t, i18n } = useTranslation()
  const { org, deviceId, can } = useClub()
  const persons = usePersons()
  const s = useSensitive()
  const [rates, setRates] = useState<Rate[] | null>(null)
  const [f, setF] = useState({ person: '', amount: '', unit: 'hour' as 'hour' | 'month' })
  const rateId = useRef(newId())

  const load = async () => { const r = await s.call<Rate[]>('report_pay_rates', { p_org: org.org_id, p_device_id: deviceId }); if (r) setRates(r) }
  const amount = parseDecimal(f.amount)
  if (!can('pay.read')) return <NotAllowed permission="pay.read" />
  const incomplete = !f.person || amount === null
  return (
    <section className="flex flex-col gap-6" data-testid="pay-page">
      <BackLink to="/settings">← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold">{t('pay.title')}</h1>
      <Banner tone="info">{t('pay.help')}</Banner>
      <NoFactorBanner testId="pay-no-factor" />
      <StepUpField s={s} />
      <button className="btn" disabled={s.busy || !s.online} onClick={() => void load()} data-testid="pay-show">{t('pay.show')}</button>
      {rates && (
        <ul className="flex flex-col gap-2" data-testid="pay-list">
          {rates.length === 0 && <li className="opacity-70">{t('pay.none')}</li>}
          {rates.map((r) => <li key={r.person_id} className="card flex justify-between gap-3" data-testid="pay-row"><span className="font-semibold">{r.display_name}</span><span>{fmtNum(r.amount, i18n.language)} € / {t(`pay.unit.${r.unit}`)}</span></li>)}
        </ul>
      )}
      {can('pay.manage') && (
        <form className="card flex flex-col gap-3" data-testid="pay-form" onSubmit={(e) => {
          e.preventDefault()
          void s.command('pay_rate.set@1', { rate_id: rateId.current, person_id: f.person, amount: amount!, unit: f.unit }, 'pay.saved').then((ok) => { if (ok) { rateId.current = newId(); setF({ ...f, amount: '' }); if (rates) void load() } })
        }}>
          <h2 className="text-lg font-bold">{t('pay.set')}</h2>
          <Field label={t('qualif.person')} htmlFor="pay-person"><select id="pay-person" className="input" value={f.person} onChange={(e) => setF({ ...f, person: e.target.value })} data-testid="pay-person"><option value="">{t('mounts.choose')}</option>{(persons ?? []).filter((p) => !isErased(p)).map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}</select></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('pay.amount')} htmlFor="pay-amount"><input id="pay-amount" className="input" inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="pay-amount" /></Field>
            <Field label={t('pay.per')} htmlFor="pay-unit"><select id="pay-unit" className="input" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value as 'hour' | 'month' })} data-testid="pay-unit"><option value="hour">{t('pay.unit.hour')}</option><option value="month">{t('pay.unit.month')}</option></select></Field>
          </div>
          {/* M23 item 6: why the primary is still grey, next to it */}
          <DisabledHint show={incomplete} testId="pay-save-hint">{t('pay.saveHint')}</DisabledHint>
          <button className="btn btn-primary" disabled={s.busy || !s.online || incomplete} data-testid="pay-save">{t('pay.save')}</button>
        </form>
      )}
    </section>
  )
}
